// React port of Angular ProductSelectionStateService. Provides a
// process-wide singleton store for in-progress product configuration
// (selected product/style/preferences/measurements). All ecommerce
// blocks on a page share this store — that's what lets ProductPreview
// update live as the assistant tweaks ProductSelection. The cart array
// itself lives on a page variable (per the block's cartBinding); only
// the unsaved configuration draft lives here.

import { useSyncExternalStore } from 'react'
import type {
  ErpFieldDef, ErpMeasurementSchema, ErpPreferenceSchema, ErpProduct, ErpStyle,
} from './erpService'

export interface AnchorPatch {
  x?: number
  y?: number
  anchor?: string
  scale?: number
  [k: string]: unknown
}

export interface PlacementOverride {
  productId: string
  anchors: Record<string, AnchorPatch>
}

export interface ProductSelectionState {
  modalOpen: boolean
  cartDrawerOpen: boolean
  editingCartItemId: string | null
  selectedProduct: ErpProduct | null
  selectedStyle: ErpStyle | null
  prefSchema: ErpPreferenceSchema | null
  measSchema: ErpMeasurementSchema | null
  prefValues: Record<string, unknown>
  measValues: Record<string, number | ''>
  prefErrors: Record<string, string>
  measErrors: Record<string, string>
  qty: number
  unitPrice: number
  placementOverrides: PlacementOverride | null
}

const initialState: ProductSelectionState = {
  modalOpen: false,
  cartDrawerOpen: false,
  editingCartItemId: null,
  selectedProduct: null,
  selectedStyle: null,
  prefSchema: null,
  measSchema: null,
  prefValues: {},
  measValues: {},
  prefErrors: {},
  measErrors: {},
  qty: 1,
  unitPrice: 0,
  placementOverrides: null,
}

class Store {
  private state: ProductSelectionState = { ...initialState }
  private listeners = new Set<() => void>()

  get(): ProductSelectionState {
    return this.state
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => { this.listeners.delete(fn) }
  }

  private set(patch: Partial<ProductSelectionState>) {
    this.state = { ...this.state, ...patch }
    for (const fn of this.listeners) fn()
  }

  setModalOpen(v: boolean) { this.set({ modalOpen: v }) }
  setCartDrawerOpen(v: boolean) { this.set({ cartDrawerOpen: v }) }
  toggleCartDrawer() { this.set({ cartDrawerOpen: !this.state.cartDrawerOpen }) }

  setSelectedProduct(p: ErpProduct | null) { this.set({ selectedProduct: p }) }
  setSelectedStyle(s: ErpStyle | null) { this.set({ selectedStyle: s }) }
  setPrefSchema(s: ErpPreferenceSchema | null) { this.set({ prefSchema: s }) }
  setMeasSchema(s: ErpMeasurementSchema | null) { this.set({ measSchema: s }) }
  setQty(n: number) { this.set({ qty: Math.max(1, n) }) }
  bumpQty(delta: number) { this.set({ qty: Math.max(1, this.state.qty + delta) }) }
  setUnitPrice(raw: number | string) {
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (Number.isFinite(n) && n >= 0) this.set({ unitPrice: n })
  }

  setPref(key: string, value: unknown) {
    const errs = { ...this.state.prefErrors }
    delete errs[key]
    this.set({ prefValues: { ...this.state.prefValues, [key]: value }, prefErrors: errs })
  }

  setMeas(key: string, raw: string) {
    const num: number | '' = raw === '' ? '' : Number(raw)
    const errs = { ...this.state.measErrors }
    delete errs[key]
    this.set({ measValues: { ...this.state.measValues, [key]: num as number }, measErrors: errs })
  }

  setPlacementOverride(o: PlacementOverride) { this.set({ placementOverrides: o }) }

  clearPlacementOverride(productId?: string) {
    const cur = this.state.placementOverrides
    if (!cur) return
    if (productId && cur.productId !== productId) return
    this.set({ placementOverrides: null })
  }

  openModal() { this.set({ modalOpen: true }) }
  closeModal() { this.set({ modalOpen: false }) }

  resetConfiguration() {
    this.set({
      selectedProduct: null,
      selectedStyle: null,
      prefSchema: null,
      measSchema: null,
      prefValues: {},
      measValues: {},
      prefErrors: {},
      measErrors: {},
      qty: 1,
      unitPrice: 0,
      placementOverrides: null,
      editingCartItemId: null,
    })
  }

  resetStyle() {
    const p = this.state.selectedProduct
    this.set({
      selectedStyle: null,
      prefSchema: null,
      measSchema: null,
      prefValues: {},
      measValues: {},
      prefErrors: {},
      measErrors: {},
      unitPrice: p ? p.basePrice : 0,
    })
  }

  /** Clear the in-progress customise inputs without touching the
   *  selected product/style or unit price. Called by pickStyle so the
   *  form starts blank when a new style is chosen. */
  clearCustomiseInputs() {
    this.set({
      prefSchema: null,
      measSchema: null,
      prefValues: {},
      measValues: {},
      prefErrors: {},
      measErrors: {},
    })
  }

  beginEdit(item: {
    cartItemId?: string
    productId?: string
    styleId?: string
    qty?: number
    unitPrice?: number
    preferences?: Record<string, unknown>
    measurements?: Record<string, number>
    placementOverrides?: Record<string, AnchorPatch>
  }) {
    if (!item.cartItemId) return
    const meas: Record<string, number | ''> = {}
    for (const [k, v] of Object.entries(item.measurements ?? {})) meas[k] = v
    this.set({
      editingCartItemId: item.cartItemId,
      qty: Number(item.qty) || 1,
      unitPrice: Number(item.unitPrice) || 0,
      prefValues: { ...(item.preferences ?? {}) },
      measValues: meas,
      prefErrors: {},
      measErrors: {},
      placementOverrides:
        item.placementOverrides && item.productId
          ? { productId: item.productId, anchors: item.placementOverrides }
          : null,
    })
  }

  cancelEdit() { this.set({ editingCartItemId: null }) }

  validate(): boolean {
    const prefErrors: Record<string, string> = {}
    const measErrors: Record<string, string> = {}

    const prefs = this.state.prefSchema
    if (prefs) {
      for (const f of prefs.fields) {
        if (f.required) {
          const v = this.state.prefValues[f.key]
          if (v === undefined || v === null || v === '') prefErrors[f.key] = 'Required'
        }
      }
    }

    const meas = this.state.measSchema
    if (meas) {
      for (const g of meas.groups) {
        for (const f of g.fields) {
          const raw = this.state.measValues[f.key]
          if (f.required && (raw === undefined || raw === '')) {
            measErrors[f.key] = 'Required'
            continue
          }
          if (raw !== undefined && raw !== '') {
            const n = Number(raw)
            if (!Number.isFinite(n)) measErrors[f.key] = 'Number'
            else if (f.min !== undefined && n < f.min) measErrors[f.key] = `Min ${f.min}`
            else if (f.max !== undefined && n > f.max) measErrors[f.key] = `Max ${f.max}`
          }
        }
      }
    }

    this.set({ prefErrors, measErrors })
    return Object.keys(prefErrors).length === 0 && Object.keys(measErrors).length === 0
  }

  buildCartItem(): Record<string, unknown> | null {
    const product = this.state.selectedProduct
    const style = this.state.selectedStyle
    if (!product || !style) return null

    const override = this.state.placementOverrides
    const placementOverrides = override && override.productId === product.id ? override.anchors : undefined

    const editingId = this.state.editingCartItemId
    const cartItemId = editingId ?? `ci_${Date.now()}_${Math.floor(Math.random() * 10000)}`

    const item: Record<string, unknown> = {
      cartItemId,
      productId: product.id,
      productName: product.name,
      styleId: style.id,
      styleName: style.name,
      imageUrl: product.imageUrl,
      preferences: { ...this.state.prefValues },
      measurements: this.normalisedMeasurements(),
      qty: this.state.qty,
      unitPrice: this.state.unitPrice,
      currency: product.currency,
      customisationSummary: this.buildSummary(),
    }
    if (placementOverrides) item.placementOverrides = placementOverrides
    return item
  }

  /** Push the in-progress configuration onto the supplied cart array. */
  pushToCart(currentCart: unknown): { cart: Record<string, unknown>[]; item: Record<string, unknown> } | null {
    const item = this.buildCartItem()
    if (!item) return null
    const cart = Array.isArray(currentCart) ? [...(currentCart as Record<string, unknown>[])] : []
    const editingId = this.state.editingCartItemId
    if (editingId) {
      const idx = cart.findIndex((c) => c?.cartItemId === editingId)
      if (idx >= 0) cart[idx] = item
      else cart.push(item)
    } else {
      cart.push(item)
    }
    // The diff is now committed onto the cart line — clear it so a
    // subsequent customisation pass starts fresh from server defaults.
    const productId = this.state.selectedProduct?.id
    this.set({
      editingCartItemId: null,
      placementOverrides:
        productId && this.state.placementOverrides?.productId === productId
          ? null
          : this.state.placementOverrides,
    })
    return { cart, item }
  }

  private normalisedMeasurements(): Record<string, number> {
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(this.state.measValues)) {
      if (v === '' || v === undefined) continue
      const n = Number(v)
      if (Number.isFinite(n)) out[k] = n
    }
    return out
  }

  private buildSummary(): string {
    const parts: string[] = []
    const style = this.state.selectedStyle
    if (style?.name) parts.push(style.name)

    const prefs = this.state.prefSchema
    if (prefs) {
      for (const f of prefs.fields) {
        const v = this.state.prefValues[f.key]
        if (v === undefined || v === null || v === '') continue
        const opt = (f.options || []).find((o) => o.value === v)
        parts.push(opt ? opt.label : String(v))
      }
    }
    const meas = this.state.measSchema
    if (meas) {
      const flat: ErpFieldDef[] = meas.groups.flatMap((g) => g.fields)
      const required = flat.filter((f) => f.required).slice(0, 3)
      for (const f of required) {
        const v = this.state.measValues[f.key]
        if (v === undefined || v === '') continue
        const unit = f.unit || meas.unit
        parts.push(`${f.label} ${v}${unit || ''}`)
      }
    }
    return parts.join(' · ')
  }
}

const store = new Store()

export const productSelectionStore = store

export function useProductSelectionState(): ProductSelectionState {
  return useSyncExternalStore(store.subscribe, store.get.bind(store), store.get.bind(store))
}
